import { prefersReducedMotion } from '@theme/utilities';

/**
 * <viewport-autoplay-media>
 *
 * Wraps a single media element and toggles playback based on whether the wrapper
 * is in the viewport.
 *
 * Supported children (first match wins):
 * - <video>
 * - <iframe data-video-type="youtube|vimeo"> (uses postMessage API)
 *
 * Attributes (all optional):
 * - play-on-entry: defaults to true. Set to "false" to disable autoplay on enter.
 * - stop-on-exit: defaults to true. Set to "false" to disable pausing on exit.
 * - defer-load: defaults to true. Set to "false" to keep a VISIBLE video's
 *   parser-initiated download running (LCP hero) instead of deferring it until
 *   viewport entry. Hidden breakpoint variants are still deferred.
 *
 * Usage:
 * <viewport-autoplay-media play-on-entry stop-on-exit>
 *   {{ video | video_tag: muted: true, autoplay: true, loop: true }}
 * </viewport-autoplay-media>
 */
class ViewportAutoplayMedia extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLVideoElement | null} Video whose sources are stashed until in view. */
  #deferredVideo = null;

  connectedCallback() {
    const media = this.#getMediaElement();
    if (!media) return;

    // defer the video download until the wrapper enters the viewport.
    // The <video> keeps its `autoplay` attribute (so it self-starts once a source
    // is attached), but its <source> URLs are stashed to `data-src` and any
    // parse-time fetch is aborted, so off-screen / hidden videos load nothing
    // until scrolled into view. Sources are restored + played on entry.
    // With defer-load="false" a VISIBLE video keeps its parser-initiated,
    // early-discovered download (the LCP hero case); a hidden breakpoint
    // variant is still deferred so it doesn't double-download.
    if (media instanceof HTMLVideoElement) {
      const deferLoad = this.#booleanAttribute('defer-load', true);
      const isVisible = media.getClientRects().length > 0;
      if (deferLoad || !isVisible) this.#stashVideoSources(media);
      // `video_tag` renders a poster fallback <img> with no `alt`, which trips
      // the axe `image-alt` check. These are decorative background videos, so
      // mark the poster image decorative.
      const posterImg = media.querySelector('img:not([alt])');
      if (posterImg) posterImg.setAttribute('alt', '');
    }

    if (prefersReducedMotion()) return;

     const playOnEntry = this.#booleanAttribute('play-on-entry', true);
     const stopOnExit = this.#booleanAttribute('stop-on-exit', true);

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Hardcoded config (per request)
    const threshold = 0.25;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
          if (playOnEntry) this.#play(media);
        } else {
          if (stopOnExit) this.#pause(media);
        }
      },
      {
        threshold: [0, threshold, 0.5, 0.75, 1],
      }
    );

    this.#observer.observe(media);

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden && stopOnExit) this.#pause(media);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;

    this.#abortController?.abort();
    this.#abortController = null;
  }

  /** @returns {HTMLVideoElement | HTMLIFrameElement | null} */
  #getMediaElement() {
    const video = this.querySelector('video');
    if (video instanceof HTMLVideoElement) return video;

    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe instanceof HTMLIFrameElement) return iframe;

    return null;
  }

  /**
   * Move the video's source URLs out of `src` (into `data-src`) and abort any
   * in-flight load so an off-screen video downloads nothing until it is in view.
   * @param {HTMLVideoElement} video
   */
  #stashVideoSources(video) {
    let stashed = false;

    for (const source of video.querySelectorAll('source[src]')) {
      source.dataset.src = source.getAttribute('src') ?? '';
      source.removeAttribute('src');
      stashed = true;
    }

    const inlineSrc = video.getAttribute('src');
    if (inlineSrc) {
      video.dataset.src = inlineSrc;
      video.removeAttribute('src');
      stashed = true;
    }

    if (!stashed) return;

    this.#deferredVideo = video;
    video.preload = 'none';
    // Reset the media element to a no-source state, cancelling any fetch the
    // parser may have kicked off before this module ran.
    video.load();
  }

  /**
   * Re-attach the stashed source URLs and (re)load the video. Called on the
   * first viewport entry, just before play().
   * @param {HTMLVideoElement} video
   */
  #restoreVideoSources(video) {
    if (this.#deferredVideo !== video) return;

    for (const source of video.querySelectorAll('source[data-src]')) {
      source.setAttribute('src', source.dataset.src ?? '');
      delete source.dataset.src;
    }

    if (video.dataset.src) {
      video.setAttribute('src', video.dataset.src);
      delete video.dataset.src;
    }

    this.#deferredVideo = null;
    video.load();
  }

  /**
   * Boolean attribute parser.
   * - Missing attribute -> defaultValue
   * - Present with value "false" -> false
   * - Present ("", "true", or any other value) -> true
   * @param {string} name
   * @param {boolean} defaultValue
   */
  #booleanAttribute(name, defaultValue) {
    if (!this.hasAttribute(name)) return defaultValue;
    return this.getAttribute(name) !== 'false';
  }

  /** @param {HTMLVideoElement | HTMLIFrameElement} media */
  #play(media) {
    if (media instanceof HTMLVideoElement) {
      // Attach the deferred sources on first entry, then play.
      if (this.#deferredVideo === media) this.#restoreVideoSources(media);
      // Autoplay policy: muted + playsInline
      media.muted = true;
      media.playsInline = true;
      void media.play().catch(() => {});
      return;
    }

    const videoType = media.dataset.videoType;
    if (!videoType) return;

    media.contentWindow?.postMessage(
      videoType === 'youtube'
        ? '{"event":"command","func":"playVideo","args":""}'
        : '{"method":"play"}',
      '*'
    );
  }

  /** @param {HTMLVideoElement | HTMLIFrameElement} media */
  #pause(media) {
    if (media instanceof HTMLVideoElement) {
      media.pause();
      return;
    }

    const videoType = media.dataset.videoType;
    if (!videoType) return;

    media.contentWindow?.postMessage(
      videoType === 'youtube'
        ? '{"event":"command","func":"pauseVideo","args":""}'
        : '{"method":"pause"}',
      '*'
    );
  }
}

if (!customElements.get('viewport-autoplay-media')) {
  customElements.define('viewport-autoplay-media', ViewportAutoplayMedia);
}
